<template>
    <Teleport to="#feedback-link"><a href="#" @click.prevent="popModal">{{ translate('Feedback') }}</a></Teleport>
    <Teleport to="body">
        <div id="feedbackModal" ref="feedbackModal" class="modal fade port-modal" role="dialog" tabindex="-1" 
             aria-labelledby="feedback-modal-title" aria-hidden="true">
            <div class="modal-dialog">
                <div class="modal-content">
                    <div class="modal-header">
                        <button type="button" class="close" data-bs-dismiss="modal">{{ translate('Close') }}</button>
                    </div>
                    <h5 id="feedback-modal-title" class="modal-title">{{ translate('Website Feedback') }}</h5>
                    <div class="modal-body">
                        <div>
                            <p>
                                <strong>{{ translate('Do you have feedback about this website? Please send us a message. If you would like a response, please also include your name and contact info.') }}</strong>
                            </p>
                        </div>

                        <div>
                            <form v-show="!submitted" id="feedback-form" @submit.prevent="submitFeedback">

                                <div class="mb-2">
                                    <textarea rows="5" class="form-control" :class="{ 'is-invalid': v$.feedback.$error }" v-model="feedback"></textarea>
                                    <div v-show="v$.feedback.$error" class="invalid-feedback">{{ translate('Please enter your comment.') }}</div>
                                </div>

                                <div>
                                    <Recaptcha ref="recaptchaEl" :site-key="props.RecaptchaSiteKey" @solved="setRecaptchaResponse"></Recaptcha>
                                    <label class="invalid-feedback" :class="{ 'd-block': v$.recaptchaResult.$error }">{{ translate('Prove that you are not a robot.') }}</label>
                                </div>
                            </form>
                            <div v-show="submitted">{{ translate('Thank you for providing feedback.') }}</div>
                        </div>

                        <div class="mt-3">
                            <div id="feedback-error" class="alert alert-danger d-none" role="alert"></div>
                            <div id="feedback-success" class="alert alert-success d-none" role="alert"></div>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button v-if="!submitted" id="submit-feedback-btn" type="button" class="btn btn-primary" :disabled="submitting ? 'disabled' : null"
                                @click="submitFeedback">{{ translate(submitting ? 'Submitting...' : 'Submit') }}</button>
                        <button type="button" class="btn btn-primary" v-else data-bs-dismiss="modal">{{ translate('Close') }}</button>
                    </div>
                </div>
            </div>
        </div>
    </Teleport>
</template>

<script setup>
    import { ref, onMounted, onBeforeUnmount } from 'vue';
    import Recaptcha from '../../recaptcha.vue';
    import useVuelidate from '@vuelidate/core';
    import { required } from '@vuelidate/validators';
    import { useTranslations } from '../useTranslations';

    const Modal = window.bootstrap?.Modal;

    const props = defineProps(['AntiForgeryToken', 'RecaptchaSiteKey', 'FeedbackUrl', 'Resources']);

    const feedback = ref('');
    const recaptchaResult = ref('');
    const feedbackModal = ref(null);
    const recaptchaEl = ref(null);
    const submitting = ref(false);
    const submitted = ref(false);

    const rules = {
        feedback: { required },
        recaptchaResult: { required }
    }

    const v$ = useVuelidate(rules, {
        feedback,
        recaptchaResult
    });

    const translate = useTranslations(props.Resources);

    function popModal() {
        const modal = Modal.getOrCreateInstance(feedbackModal.value);
        modal.show();
    }

    function setRecaptchaResponse(solved) {
        recaptchaResult.value = solved ? recaptchaEl.value.getResponse() : null;
    }

    async function submitFeedback() {
        const isValid = await v$.value.$validate();
        if (!isValid) {
            return;
        }
        submitting.value = true;

        const formData = new URLSearchParams();
        formData.append('feedback', feedback.value);
        formData.append('__RequestVerificationToken', props.AntiForgeryToken);
        formData.append('g-recaptcha-response', recaptchaResult.value);

        fetch(props.FeedbackUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: formData.toString()
        })
        .then(() => {
            submitting.value = false;
            submitted.value = true;
        })
        .catch(ex => {
            console.log(ex);
            submitting.value = false;
        });
    }

    // Add modal close event listener and dispose logic
    let modalCloseHandler = null;

    onMounted(() => {
        modalCloseHandler = () => {
            submitting.value = false;
            submitted.value = false;
            v$.value.$reset();
        };
        feedbackModal.value?.addEventListener('hidden.bs.modal', modalCloseHandler);
    });

    onBeforeUnmount(() => {
        if (feedbackModal.value && modalCloseHandler) {
            feedbackModal.value.removeEventListener('hidden.bs.modal', modalCloseHandler);
        }
    });
</script>